import { useEffect, useState } from "react";

import { EMPTY_OTP, OtpInput } from "@/components/OtpInput";
import { FormError, PublicShell } from "@/auth/PublicShell";
import { auth, usingOrbitIdentity } from "@/api/client";
import { startEnrol, verifyEnrol, type EnrolStart } from "@/auth/public_api";

/** First-time two-factor setup — scan, verify, save backup codes.
 *
 *  Reached when sign-in answers `MFA_ENROLMENT_REQUIRED`: the password was
 *  correct, and the account may not proceed without an authenticator. Before
 *  this page existed that code was a DEAD END — the login screen said "set up
 *  two-factor authentication" and offered no way to do it.
 *
 *  THE SECRET IS HELD SERVER-SIDE UNTIL A CODE VERIFIES AGAINST IT. Fabric keeps
 *  it in the session (`mfa_enrol_secret`), so a reload re-offers the SAME secret
 *  instead of silently re-keying the authenticator the person just scanned —
 *  which would leave them holding an entry that can never work.
 *
 *  THE 'CONTINUE' AFTER BACKUP CODES IS NOT A UI STEP. It calls `finish-enrol`,
 *  which is guarded by a single-use server flag set ONLY after a successful TOTP
 *  verify in this same session. That guard is a patched MFA BYPASS: the route was
 *  once reachable from a password-only session and handed out a full admin
 *  session with no code ever verified, for every already-enrolled admin. So this
 *  page must never treat "the user saw the backup codes screen" as proof of
 *  anything — the proof is server-side, and a refusal here is correct behaviour,
 *  not an error to route around.
 */
export function MfaEnrolPage({ loginId, onEnrolled }: {
  loginId: string;
  onEnrolled: () => void;
}) {
  const [step, setStep] = useState<"scan" | "codes">("scan");
  const [enrol, setEnrol] = useState<EnrolStart | null>(null);
  const [otp, setOtp] = useState<string[]>(EMPTY_OTP);
  const [codes, setCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const [manual, setManual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // THE REAL SERVER when Orbit owns identity, the stage-A fixture otherwise.
    //
    // This branch is not cosmetic. The fixture returns a HARDCODED secret, so
    // against a real backend it would show a QR for a secret the server has
    // never heard of — the person scans it, gets six perfectly plausible
    // digits, and every one is refused, with nothing anywhere saying why.
    const start = usingOrbitIdentity()
      ? auth.mfaBegin().then((r) => ({ ...r, qr_png_path: r.qr_png }))
      : startEnrol(loginId);
    start.then(setEnrol).catch(() => setError("Could not start setup. Refresh and try again."));
  }, [loginId]);

  async function verify(code: string) {
    if (busy) return;                     // a burned timestep cannot be re-sent
    setBusy(true);
    setError("");
    try {
      const res = usingOrbitIdentity()
        ? await auth.mfaFinish(enrol!.secret, code)
        : await verifyEnrol(code);
      setCodes(res.backup_codes);
      setStep("codes");
    } catch {
      setError("That code didn't verify. Try the next one — and check your phone's clock is set automatically.");
      setOtp(EMPTY_OTP);
    } finally {
      setBusy(false);
    }
  }

  if (step === "codes") {
    return (
      <PublicShell
        eyebrow="Orbit Platform"
        title="Save your backup codes"
        sub="Each code signs you in once if you lose your phone. This is the only time they are shown."
      >
        <div className="panel panel-pad" style={{ marginBottom: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {codes.map((c) => <span key={c} className="mono" style={{ fontSize: 13 }}>{c}</span>)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button type="button" className="btn ghost sm" style={{ flex: 1 }}
            onClick={() => void navigator.clipboard?.writeText(codes.join("\n"))}>
            Copy
          </button>
          <button type="button" className="btn ghost sm" style={{ flex: 1 }}
            onClick={() => {
              const blob = new Blob([codes.join("\n")], { type: "text/plain" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = "orbit-backup-codes.txt";
              a.click();
              URL.revokeObjectURL(a.href);
            }}>
            Download
          </button>
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, marginBottom: 12 }}>
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
          <span>I have saved these somewhere I can reach without my phone.</span>
        </label>
        <button className="btn-signin" type="button" disabled={!saved} onClick={onEnrolled}>
          Continue to Orbit →
        </button>
        <div className="cap" style={{ marginTop: 12, textAlign: "center" }}>
          We emailed you a security alert about this enrolment. If it wasn&rsquo;t you,
          act on that email — someone else knows your password.
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell
      eyebrow="Orbit Platform"
      title="Set up two-factor"
      sub="Scan this with Google Authenticator, 1Password, or any TOTP app, then enter the code it shows."
    >
      {!enrol ? (
        <div className="cap" style={{ textAlign: "center" }}>Preparing…</div>
      ) : (
        <>
          <div style={{ display: "grid", placeItems: "center", marginBottom: 12 }}>
            <img
              src={enrol.qr_png_path}
              alt="Two-factor setup QR code"
              width={168}
              height={168}
              style={{ background: "#fff", padding: 8, borderRadius: 8 }}
            />
          </div>

          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <button type="button" className="btn ghost sm" onClick={() => setManual((v) => !v)}>
              {manual ? "Hide setup key" : "Can't scan? Enter a key instead"}
            </button>
            {manual && (
              <div className="panel panel-pad mono" style={{ marginTop: 8, fontSize: 13, wordBreak: "break-all" }}>
                {enrol.secret}
              </div>
            )}
          </div>

          <div className="igroup">
            <label>Code from your authenticator</label>
            <OtpInput
              value={otp}
              onChange={setOtp}
              onComplete={(code) => void verify(code)}
              autoFocus
              disabled={busy}
            />
          </div>

          {error && <FormError>{error}</FormError>}

          <div className="cap" style={{ textAlign: "center" }}>
            {busy ? "Checking…" : "The code changes every 30 seconds — if one fails, wait for the next."}
          </div>
        </>
      )}
    </PublicShell>
  );
}
